/**
 * Table controls bar for the summary pages: the plan-by, aggregation and
 * intake tabs, then the View picker, and the graph toggle at the far right.
 * Which KPI columns show is the View's to say, so there is no category strip
 * and no OPT toggle.
 */
import { IconButton, Tabs, TabsList } from "@appretio/ui-kit";
import { SummaryViewSelector } from "../../summaryViews/SummaryViewSelector";
import type { SummaryViewState } from "../../summaryViews/useSummaryViewState";
import type {
  AggView,
  PlanByMode
} from "../../utils/summary/summaryPageHelpers";
import { isIndexFamilyMode } from "../cubeDataTable/utils/indexUtils";
import type { IntakeSource } from "../cubeDataTable/utils/intakeUtils";
import { IntakeSourceTabs } from "../intakeSourceTabs/IntakeSourceTabs";
import { DEFAULT_FORECAST_TABLE_CONTROL_OPTIONS } from "../tableControls/forecastTableControlValues";
import {
  tableControlsContainer,
  tableControlsLeft,
  tableControlsRight,
  viewPickerSlot
} from "./SummaryTableControls.css";

const AGG_TRIGGERS = ["PTD", "PTG", "TTL", "ALL"];

/** Generic value+onChange pair for a controlled tab/checkbox. */
interface Controlled<T> {
  /** Current value. */
  value: T;
  /** Change handler. */
  onChange: (value: T) => void;
}

/** Plan-by control: includes a flag that gates the index-family options. */
interface PlanByControl extends Controlled<PlanByMode> {
  /** Whether index data is loaded (required for IDX and +/- Var). */
  hasIndexData: boolean;
}

/** Intake control: the toggle is disabled until a second source has a cache. */
interface IntakeControl extends Controlled<IntakeSource> {
  /** The sources the loaded data can switch between. */
  availableSources: IntakeSource[];
  /** The tabs shown; every source by default. */
  sources?: readonly IntakeSource[];
}

/** Graph toggle control. */
interface GraphControl {
  /** Whether the graph is currently visible. */
  visible: boolean;
  /** Toggle handler. */
  onToggle: () => void;
  /** When false, the toggle button is disabled (no index data to graph). */
  hasIndexData: boolean;
}

/** Props for the SummaryTableControls component. */
export interface SummaryTableControlsProps {
  /** The page's Views layer, which owns the KPI columns. */
  views: SummaryViewState;
  /** Plan-by mode control with index-data gating. */
  planBy: PlanByControl;
  /** Aggregation view control. */
  agg: Controlled<AggView>;
  /** Intake mode control with planned-cache gating. */
  intake: IntakeControl;
  /** Graph visibility / toggle control. */
  graph: GraphControl;
}

export function SummaryTableControls({
  views,
  planBy,
  agg,
  intake,
  graph
}: SummaryTableControlsProps) {
  return (
    <div className={tableControlsContainer}>
      <div className={tableControlsLeft}>
        <Tabs
          value={planBy.value}
          onValueChange={(value) => {
            const mode = value as PlanByMode;
            if (isIndexFamilyMode(mode) && !planBy.hasIndexData) return;
            planBy.onChange(mode);
          }}
        >
          <TabsList triggers={DEFAULT_FORECAST_TABLE_CONTROL_OPTIONS.planBy} />
        </Tabs>
        <Tabs
          value={agg.value}
          onValueChange={(value) => agg.onChange(value as AggView)}
        >
          <TabsList triggers={AGG_TRIGGERS} />
        </Tabs>
        <IntakeSourceTabs
          value={intake.availableSources.length > 0 ? intake.value : undefined}
          onChange={intake.onChange}
          availableSources={intake.availableSources}
          sources={intake.sources}
          disabled={intake.availableSources.length < 2}
        />
        <SummaryViewSelector views={views} slotClassName={viewPickerSlot} />
      </div>
      <div className={tableControlsRight}>
        <IconButton
          iconName="graph"
          variant="tertiary"
          size="sm"
          title="Toggle graph"
          aria-label={graph.visible ? "Hide graph" : "Show graph"}
          disabled={!graph.hasIndexData}
          onClick={graph.onToggle}
        />
      </div>
    </div>
  );
}
